// apps/console/src/services/api/optimistic-alert-removal.ts
//
// Close an alert on screen the instant the operator acts, before the server confirms it.
//
// Waiting for the reply made every answer to an emergency feel broken: Dismiss, Call back, Accept and
// Decline each sat for the length of a round-trip, seconds on a cold Lambda, with nothing moving.
// Making the screen honest about that wait is not the same as making the operator wait for it. A
// removal here is two steps. The first happens on the click. The second, `settle`, happens when the
// server replies: confirmed, it stands; refused, the alert comes back.

import { startPendingRemoval, takePendingRemoval } from './robot-alert-pending'
import { emitRobotAlert, readRobotAlerts, removeRobotAlert } from './robot-alerts-store'

/** Report how the closing request ended: `true` when the server recorded it. */
export type SettleRemoval = (confirmed: boolean) => void

/**
 * Take `alertId` off every list now, for a request that closes it on the server.
 *
 * Until `settle` runs, the store refuses every read and sighting of the alert, because the server
 * still lists it as open. Confirmed: the removal stands, tombstoned from the moment of the reply, so
 * a poll that began before the reply cannot bring it back. Refused: it is put back as last known, so
 * an answer that never reached the server never looks given. Only the first `settle` counts.
 */
export function removeRobotAlertOptimistically(alertId: string): SettleRemoval {
  const held = readRobotAlerts().find(a => a.alertId === alertId)

  // Remove FIRST: the plain removal clears any earlier hold, which must not clear this one.
  removeRobotAlert(alertId)
  startPendingRemoval(alertId, held)

  let settled = false

  return confirmed => {
    if (settled) return
    settled = true

    const taken = takePendingRemoval(alertId)

    // Another surface closed it meanwhile, and that removal is the one that stands.
    if (!taken) return

    if (confirmed) {
      // Re-stamp the tombstone at the reply. A read that began between the click and the reply can
      // still list it as open, because the server had not recorded the close yet.
      removeRobotAlert(alertId)

      return
    }

    if (taken.item) emitRobotAlert(taken.item)
  }
}
